import {
  CategoryGroupType,
  CategoryType,
  Maybe,
  ProgressType,
} from "@devforce/trailhead-client-gql";
import { I18n, TranslateOptions } from "@devforce/trailhead-client-locale";
import { CollectionItem } from "common/contentCollection";
import {
  FormControl,
  FormControlOptionGroup,
  FormControlOptionOrOptionGroup,
} from "tds/formControl";
import * as locales from "../sharedLabels/locales";

export type QueryVariables =
  | {
      filter?: {
        progress: string | null;
      };
    }
  | undefined;

enum CategoryRelationType {
  ExclusiveType = "exclusive",
  IndependentType = "independent",
}

enum DynamicValueSourceType {
  ApiNameType = "api_name",
  NameType = "name",
}

const i18n = new I18n({ filters: locales });
const t = (key: string, options?: TranslateOptions) =>
  i18n.t("filters", key, options);
enum TRANSLATION {
  MISSING = "i18n:missing",
}

/**
 * Represents a collection of filters.
 * The keys are strings representing the filter names,
 * and the values are arrays of strings representing the active filters.
 */
export type FilterCollectionType = {
  [key: string]: ProgressType | string[];
};

/**
 * Generates an array of form controls based on the provided filter response and active filters.
 * Each form control corresponds to a category group in the filter response.
 *
 * @param filterResponse - The filter response containing the category groups.
 * @param activeFilters - The active filters to be applied.
 * @param disabledFilters - The filters that should be disabled.
 * @returns An array of form controls.
 */
export function generateFilterFormControls(
  filterResponse: CategoryGroupType[],
  activeFilters: FilterCollectionType,
  disabledFilters: Set<string> = new Set(),
  showProgressFilter: boolean = false
): FormControl[] {
  const progress: FormControl = {
    id: "progress",
    label: t("filters.progress.label"),
    labelDetail: undefined,
    type: "combobox",
    multiple: false,
    selectAllEnabled: false,
    options: [
      { label: t("filters.progress.options.all"), value: "ALL" },
      {
        label: t("filters.progress.options.incomplete"),
        value: "INCOMPLETE",
      },
      { label: t("filters.progress.options.completed"), value: "COMPLETED" },
    ],
    disabled: disabledFilters.has("progress") ? true : undefined,
    value: activeFilters && activeFilters["progress"],
  };

  const result = showProgressFilter ? [progress] : [];

  return result.concat([
    ...filterResponse.map((categoryGroup) =>
      categoryGroupToFilter(categoryGroup, activeFilters, disabledFilters)
    ),
  ]);
}

/**
 * Internal Helper Methods
 */

function categoryGroupToFilter(
  categoryGroup: CategoryGroupType,
  activeFilters: FilterCollectionType,
  disabledFilters: Set<string> = new Set()
): FormControl {
  return {
    id: categoryGroup.apiName,
    label: categoryGroup.name,
    labelDetail: labelDetail(categoryGroup.apiName) || undefined,
    type: "combobox",
    multiple: isMultiple(categoryGroup.categoryRelation),
    selectAllEnabled: hasChildCategories(categoryGroup),
    options: categoryGroup.categories.map((c: CategoryType) =>
      categoryToFilterOption(c, categoryGroup.dynamicValueSource)
    ),
    disabled: disabledFilters.has(categoryGroup.apiName) ? true : undefined,
    value: activeFilters && activeFilters[categoryGroup.apiName],
  };
}

function isMultiple(relation: CategoryRelationType): boolean {
  switch (relation) {
    case CategoryRelationType.IndependentType:
      return true;
    case CategoryRelationType.ExclusiveType:
      return false;
    default:
      return false;
  }
}

// This method checks if categories have categories (i.e. Skill Sets have Skills)
// but the underlying filter logic is directed at the child categories.
export function hasChildCategories(categoryGroup: CategoryGroupType): boolean {
  return categoryGroup.categories?.some(
    (category) => category.categories && category.categories?.length > 0
  );
}

export function verifiedIncompleteResult(
  node: CollectionItem,
  progressFilter?: string | null
): boolean {
  if (!progressFilter || progressFilter !== "INCOMPLETE") return true;

  const nodeProgress = node.summary?.user;
  if (!nodeProgress || nodeProgress.percentageComplete !== 100) return true;

  return false;
}

function categoryToFilterOption(
  category: CategoryType,
  valueSource: DynamicValueSourceType
): FormControlOptionOrOptionGroup {
  const option: FormControlOptionOrOptionGroup = {
    label: category.name,
    value: formControlValue(category, valueSource),
  };

  if (category.categories && category.categories.length > 0) {
    const optionGroup = option as FormControlOptionGroup;
    optionGroup.options = category.categories.map((c: CategoryType) =>
      categoryToFilterOption(c, valueSource)
    );
  }

  return option;
}

function formControlValue(
  category: CategoryType,
  valueSource: DynamicValueSourceType
): string {
  switch (valueSource) {
    case DynamicValueSourceType.ApiNameType:
      return category.apiName;
    case DynamicValueSourceType.NameType:
      return category.name;
    default:
      return category.name;
  }
}

function labelDetail(apiName: string): Maybe<string> {
  const translation = t(`filters.${apiName}.label_detail_html`, {
    defaultValue: TRANSLATION.MISSING,
  });

  return translation === TRANSLATION.MISSING ? null : translation;
}
